import { useEffect } from "react";
import { LOGO_URL } from "@/lib/quoteContent";
import { initSidePageTracking } from "@/lib/tracker";
import { ArrowLeft } from "lucide-react";

const PILLARS = [
  {
    n: "01",
    title: "We listen first",
    body: "Before we ever pick up a camera, we get to know you. How you met, the proposal, the little in-jokes, the people who matter most. Every couple fills in our pre-questionnaire and we build a plan around what makes your day yours. If your nan hand-made the cake toppers, we know before we arrive.",
  },
  {
    n: "02",
    title: "The soundtrack is yours",
    body: "The song you walk down the aisle to. Your first dance. The track that packed the dancefloor. Your film is scored with the real music from your day, songs you've picked and will love forever, not some boring instrumental track from an artist you've never heard of.",
  },
  {
    n: "03",
    title: "And real voices on top",
    body: "Music is only half of it. Your vows, the speeches, a letter read aloud, your dad's voice cracking mid-toast. We record clean audio of every word on dedicated kit and weave it through the film, so on top of your music, it's narrated by the people who lived it.",
  },
  {
    n: "04",
    title: "Two videographers, zero staging",
    body: "With two of us covering your day, one camera can stay on you while the other catches the reactions: your mum's face during the first look, your mates during the speeches. We work quietly with long lenses, so what we capture is real. Nothing posed, nothing repeated for the camera.",
  },
  {
    n: "05",
    title: "Crafted in the edit, one of one",
    body: "This is where the story comes together. Our editor watches every second of footage, finds the storyline of your day, and builds the film around it: the anticipation, the emotion, the release. Each film is individually structured, colour graded, and cut to your music. No templates. Your film could never belong to another couple.",
  },
];

export default function Storytelling() {
  useEffect(() => initSidePageTracking("storytelling"), []);
  return (
    <div className="min-h-screen bg-[#FAF9F6]" data-testid="storytelling-page">
      {/* Hero */}
      <section className="bg-[#2F2E2E] text-white px-6 md:px-12 lg:px-24 py-24 md:py-32 text-center relative">
        <button
          type="button"
          onClick={() => window.close()}
          data-testid="storytelling-back-top-btn"
          className="absolute top-6 left-6 md:top-8 md:left-12 inline-flex items-center gap-2 text-white/70 hover:text-white border border-white/30 hover:border-white/70 px-5 py-3 text-xs tracking-overline uppercase font-medium transition-colors"
        >
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
          Back To Your Quote
        </button>
        <img
          src={LOGO_URL}
          alt="Wedding Video Essex"
          className="h-20 w-auto object-contain brightness-0 invert opacity-95 mx-auto mb-12"
        />
        <div className="text-[#CCBA56] text-xs tracking-overline uppercase font-medium mb-5">
          Story-led wedding films
        </div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-tight max-w-3xl mx-auto mb-8">
          Anyone can film a wedding.
          <br />
          <span className="italic font-light">We tell the story of yours.</span>
        </h1>
        <p className="text-white/70 max-w-2xl mx-auto text-lg leading-relaxed">
          A highlight film shows what your day looked like. A story-led film
          makes you feel it all again: the nerves in the morning, the look on
          their face, the words you'll want to hear forever. That difference
          doesn't happen by accident. Here's how we do it.
        </p>
      </section>

      {/* Pillars */}
      <section className="px-6 md:px-12 lg:px-24 py-20 md:py-28">
        <div className="max-w-4xl mx-auto space-y-16">
          {PILLARS.map((p) => (
            <div key={p.n} className="grid grid-cols-1 md:grid-cols-[100px_1fr] gap-4 md:gap-8">
              <div className="font-display text-5xl text-[#CCBA56]">{p.n}</div>
              <div>
                <h2 className="font-display text-2xl md:text-3xl text-[#1F1E1D] font-medium mb-4">
                  {p.title}
                </h2>
                <p className="text-[#5C5A56] text-base leading-relaxed font-light">
                  {p.body}
                </p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Difference strip */}
      <section className="bg-[#2F2E2E] px-6 md:px-12 lg:px-24 py-20 text-center">
        <div className="text-[#CCBA56] text-xs tracking-overline uppercase font-medium mb-6">
          The difference you'll feel
        </div>
        <blockquote className="font-display text-2xl md:text-4xl text-white italic font-light max-w-3xl mx-auto leading-snug">
          "It didn't feel like a wedding video. It felt like our day, bottled."
        </blockquote>
        <div className="text-white/50 text-xs tracking-overline uppercase font-medium mt-6">
          One of our 1000+ couples
        </div>
      </section>

      {/* CTA */}
      <section className="px-6 md:px-12 lg:px-24 py-16 text-center">
        <p className="text-[#5C5A56] text-base font-light mb-6">
          Got a quote from us? Head back to it and secure your date.
        </p>
        <button
          type="button"
          onClick={() => window.close()}
          data-testid="storytelling-back-btn"
          className="inline-flex items-center gap-2 bg-[#2F2E2E] hover:bg-[#454341] text-white px-8 py-4 text-xs tracking-overline uppercase font-medium transition-colors"
        >
          Back To Your Quote
        </button>
      </section>
    </div>
  );
}
